import * as i0 from '@angular/core';
import { Injectable, Optional, Input, Directive, NgModule, inject, ChangeDetectorRef, HostBinding, NgZone, DestroyRef, makeEnvironmentProviders, provideAppInitializer, RendererFactory2 } from '@angular/core';
import { getIconManifest } from '@skyux/icons';
import FontFaceObserver from 'fontfaceobserver';
import { take, takeUntil } from 'rxjs/operators';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';

/**
 * Provides a method for retrieving metadata about the SKY UX icon font.
 * @internal
 */
class SkyThemeIconManifestService {
    /**
     * Gets metadata about the SKY UX icon font.
     */
    getManifest() {
        return getIconManifest();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeIconManifestService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeIconManifestService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeIconManifestService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Defines properties of a SKY UX theme mode.
 */
class SkyThemeMode {
    /**
     * The preset themes available in SKY UX.
     */
    static { this.presets = {
        light: new SkyThemeMode('light', 'sky-theme-mode-light'),
        dark: new SkyThemeMode('dark', 'sky-theme-mode-dark'),
    }; }
    /**
     * Creates a new theme mode.
     * @param name The name of the theme mode.
     * @param hostClass The class on the host element which child components should reference when
     * adjusting for a specified theme mode.
     */
    constructor(name, hostClass) {
        this.name = name;
        this.hostClass = hostClass;
    }
}

/**
 * Defines properties of SKY UX theme spacing.
 */
class SkyThemeSpacing {
    /**
     * The preset spacing available in SKY UX.
     */
    static { this.presets = {
        standard: new SkyThemeSpacing('standard', 'sky-theme-spacing-standard'),
        compact: new SkyThemeSpacing('compact', 'sky-theme-spacing-compact'),
    }; }
    /**
     * Creates a new theme spacing.
     * @param name The name of the theme spacing.
     * @param hostClass The class on the host element which child components should reference when
     * adjusting for a specified theme spacing.
     */
    constructor(name, hostClass) {
        this.name = name;
        this.hostClass = hostClass;
    }
}

/**
 * Provides methods for updating and handling changes to the current theme.
 */
class SkyThemeService {
    /**
     * Notifies consumers when the current theme settings have changed.
     */
    get settingsChange() {
        return this.#_settingsObs;
    }
    #current;
    #brandLinkElement;
    #hostEl;
    #renderer;
    #settings;
    #_settingsObs;
    constructor() {
        this.#settings = new ReplaySubject(1);
        this.#_settingsObs = this.#settings.asObservable();
    }
    /**
     * Initializes the theme service with the specified parameters. This should only be called
     * from a host component that provides its own theme to child components.
     * @param hostEl The host element under which themed components are rendered.
     * @param renderer A Renderer2 instance for updating the host element with theme changes.
     * @param theme The initial theme.
     */
    init(hostEl, renderer, theme) {
        this.#hostEl = hostEl;
        this.#renderer = renderer;
        this.setTheme(theme);
    }
    /**
     * Destroys the current theme service. This should only be called from a host component that
     * provides its own theme to child components.
     */
    destroy() {
        this.#settings.complete();
        this.#hostEl = this.#renderer = undefined;
    }
    /**
     * Updates the current theme settings.
     * @param settings The new theme settings to apply.
     */
    setTheme(settings) {
        const previous = this.#current;
        this.#applyThemeClass(previous, settings, 'theme');
        this.#applyThemeClass(previous, settings, 'mode', 'supportedModes');
        this.#applyThemeClass(previous, settings, 'spacing', 'supportedSpacing');
        this.#applyThemeClass(previous, settings, 'brand');
        this.#settings.next({
            currentSettings: settings,
            previousSettings: previous,
        });
        this.#current = settings;
    }
    #applyThemeClass(previous, current, prop, supportedProp) {
        const currentSetting = current[prop];
        const previousClass = previous?.[prop]?.hostClass;
        const currentClass = currentSetting?.hostClass;
        if (!previousClass || previousClass !== currentClass) {
            this.#updateHostClass(prop, previousClass, currentClass, currentSetting, current, supportedProp);
            if (prop === 'brand') {
                if (!current.theme.supportsBranding && currentSetting) {
                    throw new Error('Branding is not supported for the given theme.');
                }
                this.#updateBrandStylesheet(current.brand, previous?.brand);
            }
        }
    }
    #updateBrandStylesheet(currentBrand, previousBrand) {
        if (currentBrand &&
            currentBrand.name !== 'blackbaud' &&
            previousBrand !== currentBrand) {
            this.#addBrandStylesheet(currentBrand);
        }
        else {
            this.#removeBrandStylesheet();
        }
    }
    #updateHostClass(prop, previousClass, currentClass, currentSetting, current, supportedProp) {
        if (previousClass) {
            this.#removeHostClass(previousClass);
        }
        if (prop === 'brand') {
            if (currentClass && !previousClass) {
                this.#addHostClass('sky-theme-brand-base');
            }
            else if (!currentClass && previousClass) {
                this.#removeHostClass('sky-theme-brand-base');
            }
        }
        if (currentClass &&
            this.#isSupportedProperty(currentSetting, current, supportedProp)) {
            this.#addHostClass(currentClass);
        }
    }
    #addBrandStylesheet(brand) {
        if (brand.name !== 'blackbaud') {
            const cssPath = `https://sky.blackbaudcdn.net/static/skyux-brand-${brand.name}/${brand.version}/assets/scss/${brand.name}.css`;
            // Create a link element via Angular's renderer to avoid SSR troubles
            this.#brandLinkElement = this.#getRenderer().createElement('link');
            // Add the style to the head section
            this.#getRenderer().appendChild(this.#hostEl, this.#brandLinkElement);
            // Set type of the link item and path to the css file
            this.#getRenderer().setProperty(this.#brandLinkElement, 'rel', 'stylesheet');
            this.#getRenderer().setProperty(this.#brandLinkElement, 'href', cssPath);
        }
    }
    #removeBrandStylesheet() {
        if (this.#brandLinkElement) {
            this.#getRenderer().removeChild(this.#hostEl, this.#brandLinkElement);
            this.#brandLinkElement = undefined;
        }
    }
    #addHostClass(className) {
        this.#getRenderer().addClass(this.#hostEl, className);
    }
    #removeHostClass(className) {
        this.#getRenderer().removeClass(this.#hostEl, className);
    }
    #getRenderer() {
        if (!this.#renderer) {
            throw new Error('Renderer is not initialized. Have you called the theme service `init` method?');
        }
        return this.#renderer;
    }
    #isSupportedProperty(currentSetting, current, supportedProp) {
        return (!supportedProp ||
            ((currentSetting instanceof SkyThemeMode ||
                currentSetting instanceof SkyThemeSpacing) &&
                current.theme[supportedProp].includes(currentSetting)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

class SkyAppStyleLoader {
    static { this.LOAD_TIMEOUT = 3000; }
    #themeSvc;
    constructor(themeSvc) {
        this.isLoaded = false;
        this.#themeSvc = themeSvc;
    }
    loadStyles() {
        if (this.isLoaded) {
            return Promise.resolve();
        }
        let themePromise;
        if (this.#themeSvc) {
            themePromise = this.#themeSvc.settingsChange.pipe(take(1)).toPromise();
        }
        else {
            themePromise = Promise.resolve();
        }
        const fontAwesome = new FontFaceObserver('FontAwesome');
        const skyuxIcons = new FontFaceObserver('skyux-icons');
        const blackbaudSans = new FontFaceObserver('BLKB Sans');
        return Promise.all([
            // Specify a character for FontAwesome since some browsers will fail to detect
            // when the font is loaded unless a known character with a different width
            // than the default is not specified.
            fontAwesome.load('\uf0fc', SkyAppStyleLoader.LOAD_TIMEOUT),
            skyuxIcons.load('\ue999', SkyAppStyleLoader.LOAD_TIMEOUT),
            blackbaudSans.load(undefined, SkyAppStyleLoader.LOAD_TIMEOUT),
            themePromise,
        ])
            .then(() => {
            this.isLoaded = true;
        })
            .catch((error) => {
            // Errors loading the font should not stop the page from rendering.
            // Passing the error along in case the client wants to do something with it.
            return Promise.resolve({
                error,
            });
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppStyleLoader, deps: [{ token: SkyThemeService, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppStyleLoader, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppStyleLoader, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: SkyThemeService, decorators: [{
                    type: Optional
                }] }] });

/**
 * Component to add classes conditionally based on the current theme.
 *
 * If the directive is within a `skyTheme` directive, it uses settings from that directive.
 */
class SkyThemeClassDirective {
    /**
     * The HTML class attribute.
     *
     * @param value
     */
    set class(value) {
        this.#removeInitialClasses(this.#initialClasses);
        this.#initialClasses = typeof value === 'string' ? value.split(/\s+/) : [];
        this.#applyInitialClasses(this.#initialClasses);
        this.#applySkyThemeClassMap(this.#skyThemeClassMap);
    }
    /**
     * An object with class names in a string for keys, and theme names (e.g. 'default' or 'modern') as values.
     *
     * @param value
     */
    set skyThemeClass(value) {
        this.#removeSkyThemeClassMap(this.#skyThemeClassMap);
        this.#applyInitialClasses(this.#initialClasses);
        this.#skyThemeClassMap = value;
        /* istanbul ignore else */
        if (this.#skyThemeClassMap) {
            this.#applySkyThemeClassMap(this.#skyThemeClassMap);
        }
    }
    set #themeSettings(settings) {
        this.#currentTheme = settings;
        this.#removeSkyThemeClassMap(this.#skyThemeClassMap);
        this.#applyInitialClasses(this.#initialClasses);
        this.#applySkyThemeClassMap(this.#skyThemeClassMap);
    }
    #currentTheme;
    #ngUnsubscribe = new Subject();
    #initialClasses = [];
    #skyThemeClassMap;
    #ngEl;
    #renderer;
    constructor(ngEl, renderer, themeSvc) {
        this.#ngEl = ngEl;
        this.#renderer = renderer;
        if (themeSvc) {
            themeSvc.settingsChange
                .pipe(takeUntil(this.#ngUnsubscribe))
                .subscribe((settingsChange) => {
                this.#themeSettings = settingsChange.currentSettings;
            });
        }
    }
    ngOnDestroy() {
        this.#ngUnsubscribe.next();
        this.#ngUnsubscribe.complete();
    }
    #applyInitialClasses(classes) {
        /* istanbul ignore else */
        if (classes) {
            classes.forEach((className) => this.#toggleClass(className, true));
        }
    }
    #applySkyThemeClassMap(skyThemeClassMap) {
        if (skyThemeClassMap) {
            const themeName = this.#currentTheme?.theme.name || 'default';
            Object.keys(skyThemeClassMap).forEach((className) => {
                const enabled = themeName === skyThemeClassMap[className];
                this.#toggleClass(className, enabled);
            });
        }
    }
    #removeInitialClasses(classes) {
        /* istanbul ignore else */
        if (classes) {
            classes.forEach((className) => this.#toggleClass(className, false));
        }
    }
    #removeSkyThemeClassMap(skyThemeClassMap) {
        if (skyThemeClassMap) {
            Object.keys(skyThemeClassMap).forEach((className) => this.#toggleClass(className, false));
        }
    }
    #toggleClass(className, enabled) {
        className = className.trim();
        /* istanbul ignore else */
        if (className) {
            className.split(/\s+/g).forEach((classNameItem) => {
                if (enabled) {
                    this.#renderer.addClass(this.#ngEl.nativeElement, classNameItem);
                }
                else {
                    this.#renderer.removeClass(this.#ngEl.nativeElement, classNameItem);
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeClassDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: SkyThemeService, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.7", type: SkyThemeClassDirective, isStandalone: false, selector: "[skyThemeClass]", inputs: { class: "class", skyThemeClass: "skyThemeClass" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeClassDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skyThemeClass]',
                    standalone: false,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: SkyThemeService, decorators: [{
                    type: Optional
                }] }], propDecorators: { class: [{
                type: Input
            }], skyThemeClass: [{
                type: Input
            }] } });

/**
 * Component that works like `ngIf` to show markup for matching theme.
 *
 * If the directive is within a `skyTheme` directive, it uses settings from that directive.
 */
class SkyThemeIfDirective {
    /**
     * A string that should match the name of a theme, `'default'` or `'modern'`.
     *
     * @param value
     */
    set skyThemeIf(value) {
        this.#context = value;
        this.#updateView();
    }
    #context;
    #currentTheme;
    #ngUnsubscribe = new Subject();
    #hasView = false;
    #templateRef;
    #viewContainer;
    constructor(templateRef, viewContainer, changeDetector, themeSvc) {
        this.#templateRef = templateRef;
        this.#viewContainer = viewContainer;
        if (themeSvc) {
            themeSvc.settingsChange
                .pipe(takeUntil(this.#ngUnsubscribe))
                .subscribe((settingsChange) => {
                this.#currentTheme = settingsChange.currentSettings;
                this.#updateView();
                // Components that use OnPush change detection will not automatically
                // check for changes when created as a result of a theme change if
                // it happens outside of an Angular change detection cycle. This
                // ensures change detection runs on those components.
                changeDetector.markForCheck();
            });
        }
    }
    ngOnDestroy() {
        this.#ngUnsubscribe.next();
        this.#ngUnsubscribe.complete();
    }
    #updateView() {
        const themeName = this.#currentTheme?.theme.name || 'default';
        const condition = this.#context && themeName === this.#context;
        if (condition && !this.#hasView) {
            this.#viewContainer.createEmbeddedView(this.#templateRef);
            this.#hasView = true;
        }
        else if (!condition && this.#hasView) {
            this.#viewContainer.clear();
            this.#hasView = false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeIfDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i0.ChangeDetectorRef }, { token: SkyThemeService, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.7", type: SkyThemeIfDirective, isStandalone: false, selector: "[skyThemeIf]", inputs: { skyThemeIf: "skyThemeIf" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeIfDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skyThemeIf]',
                    standalone: false,
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i0.ChangeDetectorRef }, { type: SkyThemeService, decorators: [{
                    type: Optional
                }] }], propDecorators: { skyThemeIf: [{
                type: Input
            }] } });

/**
 * Defines properties of a SKY UX theme.
 */
class SkyTheme {
    /**
     * The preset themes available in SKY UX.
     */
    static { this.presets = {
        default: new SkyTheme('default', 'sky-theme-default', [SkyThemeMode.presets.light], [SkyThemeSpacing.presets.standard]),
        modern: new SkyTheme('modern', 'sky-theme-modern', [SkyThemeMode.presets.light, SkyThemeMode.presets.dark], [SkyThemeSpacing.presets.standard, SkyThemeSpacing.presets.compact], true),
    }; }
    /**
     * Creates a new theme.
     * @param name The name of the theme.
     * @param hostClass The class on the host element which child components should reference when
     * adjusting for a specified theme.
     * @param supportedModes An array of modes supported by the theme.
     * @param supportedSpacing An array of spacing modes supported by the theme.
     */
    constructor(name, hostClass, supportedModes, supportedSpacing = [SkyThemeSpacing.presets.standard], supportsBranding = false) {
        this.name = name;
        this.hostClass = hostClass;
        this.supportedModes = supportedModes;
        this.supportedSpacing = supportedSpacing;
        this.supportsBranding = supportsBranding;
    }
}

/**
 * Specifies the theme and mode to be applied to a host element.
 */
class SkyThemeSettings {
    /**
     * Creates a new SkyThemeSettings instance.
     * @param theme The theme configuration.
     * @param mode The theme mode.
     */
    constructor(theme, mode, spacing = SkyThemeSpacing.presets.standard, brand) {
        this.theme = theme;
        this.mode = mode;
        this.spacing = spacing;
        this.brand = brand;
    }
}

const defaultTheme = new SkyThemeSettings(SkyTheme.presets.default, SkyThemeMode.presets.light);
/**
 * Creates a new instance of `SkyThemeService` to allow a container
 * to have its own theme.
 */
class SkyThemeDirective {
    set skyTheme(value) {
        this.skyThemeOrDefault = value || defaultTheme;
        if (this.#initialized) {
            this.#themeSvc.setTheme(this.skyThemeOrDefault);
        }
    }
    #initialized;
    #elRef;
    #renderer;
    #themeSvc;
    constructor(elRef, renderer, themeSvc) {
        this.skyThemeOrDefault = defaultTheme;
        this.#initialized = false;
        this.#elRef = elRef;
        this.#renderer = renderer;
        this.#themeSvc = themeSvc;
    }
    ngOnInit() {
        this.#themeSvc.init(this.#elRef.nativeElement, this.#renderer, this.skyThemeOrDefault);
        this.#initialized = true;
    }
    ngOnDestroy() {
        this.#themeSvc.destroy();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: SkyThemeService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.7", type: SkyThemeDirective, isStandalone: false, selector: "[skyTheme]", inputs: { skyTheme: "skyTheme" }, providers: [SkyThemeService], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skyTheme]',
                    providers: [SkyThemeService],
                    standalone: false,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: SkyThemeService }], propDecorators: { skyTheme: [{
                type: Input
            }] } });

class SkyThemeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeModule, declarations: [SkyThemeClassDirective,
            SkyThemeDirective,
            SkyThemeIfDirective], exports: [SkyThemeClassDirective, SkyThemeDirective, SkyThemeIfDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SkyThemeClassDirective,
                        SkyThemeDirective,
                        SkyThemeIfDirective,
                    ],
                    exports: [SkyThemeClassDirective, SkyThemeDirective, SkyThemeIfDirective],
                }]
        }] });

/**
 * @internal
 * Defines properties of SKY UX theme branding.
 */
class SkyThemeBrand {
    get hostClass() {
        return this.#hostClass || `sky-theme-brand-${this.name}`;
    }
    set hostClass(value) {
        this.#hostClass = value;
    }
    #hostClass;
    /**
     * Creates a new theme brand.
     * @param name The name of the theme brand.
     * @param hostClass The class on the host element which child components should reference when
     * adjusting for a specified theme brand. This defaults to `sky-theme-brand-<name>
     */
    constructor(name, version, hostClass) {
        this.name = name;
        this.version = version;
        this.hostClass = hostClass;
        if (!/\d+\.\d+\.\d+(?:-(?:alpha|beta|rc)\.\d)*$/.test(version)) {
            throw new Error(`Invalid version format "${version}" for theme brand "${name}".`);
        }
    }
}

class SkyThemeComponentClassDirective {
    #changeDetector;
    #themeService;
    constructor() {
        this.theme = 'sky-cmp-theme-default';
        this.#changeDetector = inject(ChangeDetectorRef);
        this.#themeService = inject(SkyThemeService, { optional: true });
        this.#themeService?.settingsChange
            .pipe(takeUntilDestroyed())
            .subscribe((change) => {
            this.theme = `sky-cmp-theme-${change.currentSettings.theme.name}`;
            this.#changeDetector.markForCheck();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeComponentClassDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.7", type: SkyThemeComponentClassDirective, isStandalone: true, selector: "[skyThemeClass]", host: { properties: { "class": "this.theme" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyThemeComponentClassDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skyThemeClass]',
                    standalone: true,
                }]
        }], ctorParameters: () => [], propDecorators: { theme: [{
                type: HostBinding,
                args: ['class']
            }] } });

/**
 * Provides information about the state of the application's viewport.
 */
class SkyAppViewportService {
    // ESLint doesn't recognize how this is used.
    #updateRequest;
    #reserveItems;
    #conditionallyReserveItems;
    #document;
    #ngZone;
    // Observe elements crossing the browser's viewport boundaries.
    #intersectionObserver;
    #reservedSpaces;
    constructor() {
        /**
         * Updated when the viewport becomes visible.  While the page is rendered, the
         * viewport may remain hidden as fonts and styles are loaded asynchronously;
         * this is done to avoid a FOUC (Flash Of Unstyled Content) before the fonts
         * and styles are ready.
         */
        this.visible = new ReplaySubject(1);
        // ESLint doesn't recognize how this is used.
        this.#updateRequest = undefined;
        this.#reserveItems = new Map();
        this.#conditionallyReserveItems = new Map();
        this.#document = inject(DOCUMENT);
        this.#ngZone = inject(NgZone);
        // Observe elements crossing the browser's viewport boundaries.
        this.#intersectionObserver = this.#ngZone.runOutsideAngular(() => new IntersectionObserver(() => this.#ngZone.run(() => this.#updateViewportArea()), { threshold: Array.from({ length: 101 }, (_, i) => i / 100) }));
        this.#reservedSpaces = {
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
        };
        const onScroll = () => {
            if (this.#conditionallyReserveItems.size > 0) {
                this.#updateViewportArea();
            }
        };
        this.#document.addEventListener('scroll', onScroll);
        inject(DestroyRef).onDestroy(() => {
            /* istanbul ignore next */
            if (this.#updateRequest !== undefined) {
                cancelAnimationFrame(this.#updateRequest);
            }
            this.#intersectionObserver.disconnect();
            this.#document.removeEventListener('scroll', onScroll);
        });
    }
    /**
     * Reserves space for components docked to the left, top, right, or bottom of the viewport.
     * @param args
     */
    reserveSpace(args) {
        const item = {
            ...args,
            active: this.#isElementVisible({ ...args, active: false }),
        };
        this.#reserveItems.set(args.id, item);
        this.#watchVisibility(item);
        this.#updateViewportArea();
    }
    /**
     * Removes reserved space for a component by the ID provided when it was reserved.
     * @param id
     */
    unreserveSpace(id) {
        const args = this.#reserveItems.get(id);
        if (args?.reserveForElement) {
            this.#intersectionObserver.unobserve(args.reserveForElement);
            this.#conditionallyReserveItems.delete(args.reserveForElement);
        }
        this.#reserveItems.delete(id);
        this.#updateViewportArea();
    }
    #updateViewportArea() {
        this.#updateRequest ??= requestAnimationFrame(() => {
            this.#updateRequest = undefined;
            const reservedSpaces = {
                bottom: 0,
                left: 0,
                right: 0,
                top: 0,
            };
            for (const args of this.#reserveItems.values()) {
                args.active = this.#isElementVisible(args, reservedSpaces);
                if (args.active) {
                    reservedSpaces[args.position] += args.size;
                }
            }
            this.#reservedSpaces = reservedSpaces;
            const documentElementStyle = this.#document.documentElement.style;
            for (const [position, size] of Object.entries(reservedSpaces)) {
                documentElementStyle.setProperty(`--sky-viewport-${position}`, size + 'px');
            }
        });
    }
    #watchVisibility(item) {
        if (item.reserveForElement) {
            this.#conditionallyReserveItems.set(item.reserveForElement, item);
            this.#intersectionObserver.observe(item.reserveForElement);
        }
    }
    #isElementVisible(args, reservedSpaces = this
        .#reservedSpaces) {
        if (!args.reserveForElement) {
            return true;
        }
        const rect = args.reserveForElement.getBoundingClientRect();
        if (!rect || !rect.height || !rect.width) {
            return false;
        }
        const midpoint = {
            x: rect.x + Math.floor(rect.width / 2),
            y: rect.y + Math.floor(rect.height / 2),
        };
        if (
        // Vertically in view
        midpoint.y <= window.innerHeight &&
            midpoint.y >= 0 &&
            // Horizontally in view
            midpoint.x <= window.innerWidth &&
            midpoint.x >= 0) {
            // Element is not hidden by another element
            if (args.reserveForElement.contains(this.#document.elementFromPoint(midpoint.x, midpoint.y))) {
                return true;
            }
            // Check if the item's midpoint is visible based on current reserved spaces.
            // Assumes reserved spaces are intended to be additive and not overlapping.
            const threshold = reservedSpaces[args.position];
            switch (args.position) {
                case 'top':
                    return midpoint.y >= threshold;
                case 'right':
                    return midpoint.x <= window.innerWidth - threshold;
                case 'bottom':
                    return midpoint.y <= window.innerHeight - threshold;
                case 'left':
                    return midpoint.x >= threshold;
            }
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppViewportService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppViewportService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppViewportService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

function provideInitialTheme(name = 'default', options) {
    return makeEnvironmentProviders([
        SkyThemeService,
        provideAppInitializer(() => {
            const initializerFn = ((themeService, doc, rendererFactory) => {
                return () => {
                    themeService.init(doc.body, rendererFactory.createRenderer(null, null), new SkyThemeSettings(SkyTheme.presets[name], SkyThemeMode.presets[options?.mode ?? 'light'], SkyThemeSpacing.presets[options?.spacing ?? 'standard']));
                };
            })(inject(SkyThemeService), inject(DOCUMENT), inject(RendererFactory2));
            return initializerFn();
        }),
    ]);
}

/**
 * Generated bundle index. Do not edit.
 */

export { SkyAppStyleLoader, SkyAppViewportService, SkyTheme, SkyThemeBrand, SkyThemeComponentClassDirective, SkyThemeIconManifestService, SkyThemeMode, SkyThemeModule, SkyThemeService, SkyThemeSettings, SkyThemeSpacing, provideInitialTheme, SkyThemeDirective as λ1, SkyThemeClassDirective as λ2, SkyThemeIfDirective as λ3 };
